import { EditorStarterItem } from '../../items/item-type';
import {
  AudioFadableItem,
  getCanFadeAudio,
  getCanFadeVisual,
  VisuallyFadableItem,
} from '../../utils/fade';
import { EditorState, TrackType } from '../types';
import { itemOwner, trimGroup } from '@waka/compat/group-split';
import { splitItem } from './split-item';
import {preserveItemAnimationWindow} from '@waka/compat/animation-window';

export const trimClipRight = ({
  state,
  itemId,
  framePosition,
}: {
  state: EditorState;
  itemId: string;
  framePosition: number;
}): EditorState => {
  const targetItem: EditorStarterItem | undefined =
    state.undoableState.items[itemId];

  let targetTrack: TrackType | undefined;
  let targetTrackIndex = -1;

  for (let i = 0; i < state.undoableState.tracks.length; i++) {
    const track = state.undoableState.tracks[i];
    if (!track) continue;
    if (track.items.includes(itemId)) {
      targetTrack = track;
      targetTrackIndex = i;
      break;
    }
  }

  const owner = itemOwner(state, itemId);
  if (!targetTrack && owner) {
    targetTrack = owner.track;
    targetTrackIndex = state.undoableState.tracks.findIndex(
      (track) => track.id === owner.track.id,
    );
  }
  if (
    !targetTrack ||
    targetTrack.locked ||
    !targetItem ||
    targetTrackIndex === -1 ||
    !Number.isInteger(framePosition)
  ) {
    return state;
  }

  const itemStart = targetItem.from;
  const itemEnd = itemStart + targetItem.durationInFrames;

  if (framePosition <= itemStart || framePosition >= itemEnd) {
    return state;
  }
  if (targetItem.type === 'group')
    return trimGroup(state, itemId, framePosition, 'left', splitItem);

  const newDuration = framePosition - itemStart;

  const newItem: EditorStarterItem = {
    ...preserveItemAnimationWindow(targetItem,state.undoableState.fps),
    durationInFrames: newDuration,
  };

  if (getCanFadeVisual(targetItem) && !newItem.visualFadeWindow) {
    (newItem as VisuallyFadableItem).fadeOutDurationInSeconds = 0;
    (newItem as VisuallyFadableItem).fadeInDurationInSeconds = Math.min(
      (newItem as VisuallyFadableItem).fadeInDurationInSeconds,
      newDuration / state.undoableState.fps,
    );
  }

  if (getCanFadeAudio(targetItem) && !newItem.audioFadeWindow) {
    (newItem as AudioFadableItem).audioFadeOutDurationInSeconds = 0;
    (newItem as AudioFadableItem).audioFadeInDurationInSeconds = Math.min(
      (newItem as AudioFadableItem).audioFadeInDurationInSeconds,
      newDuration / state.undoableState.fps,
    );
  }

  const newItems = {
    ...state.undoableState.items,
    [itemId]: newItem,
  };

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: newItems,
    },
    selectedItems: [itemId],
  };
};
